import { type ReactNode, useId, useState } from "react";

/**
 * Evidence citations: a small numbered marker beside a value and a compact source line that opens
 * the verbatim quote. Adapted from AICSS `inline-citations` (MIT, free tier; see
 * apps/web/THIRD_PARTY_NOTICES.md): the marker and footer layout follow it, the tooltip is replaced
 * by a click-to-open quote (keyboard reachable, and the quote is the evidence, so it must be
 * readable, not a hover hint), and colours come from our tokens.
 */

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export type CiteSource = {
  n: number;
  url: string;
  title: string | null;
  sourceType: string;
  quote: string | null;
  retrievedAt?: string | null;
};

/** Marker `1` that jumps to the source line below with the same number. */
export function CiteMark({ n, targetId }: { n: number; targetId?: string }) {
  return targetId ? (
    <a className="cite-mark" href={`#${targetId}`} aria-label={`source ${n}`}>
      {n}
    </a>
  ) : (
    <span className="cite-mark" aria-hidden="true">
      {n}
    </span>
  );
}

/** One source line: marker, title, host and type; the quote opens underneath. */
export function CiteSourceLine({
  source,
  id,
  trailing,
}: {
  source: CiteSource;
  id?: string;
  /** extra links kept on the source line, so an opened quote sits below all of them */
  trailing?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const quoteId = useId();
  return (
    <div className="cite-ref" id={id}>
      <div className="cite-ref-line">
        <CiteMark n={source.n} />
        <a
          className="cite-ref-label"
          href={source.url}
          target="_blank"
          rel="noreferrer"
          title={source.url}
        >
          {source.title ?? hostOf(source.url)}
        </a>
        {source.title ? (
          <>
            <span className="cite-sep">·</span>
            <span className="cite-host">{hostOf(source.url)}</span>
          </>
        ) : null}
        <span className="cite-sep">·</span>
        <span className="cite-host">{source.sourceType}</span>
        {source.quote ? (
          <button
            type="button"
            className="cite-toggle"
            aria-expanded={open}
            aria-controls={quoteId}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? "hide quote" : "quote"}
          </button>
        ) : null}
        {trailing}
      </div>
      {open && source.quote ? (
        <blockquote id={quoteId} className="cite-quote">
          “{source.quote}”
          {source.retrievedAt ? (
            <span className="cite-when num"> retrieved {source.retrievedAt.slice(0, 10)}</span>
          ) : null}
        </blockquote>
      ) : null}
    </div>
  );
}
